import { useEffect, useState } from "react";
import { SkyText } from "@skypointcloud/skypoint-ui";
import type { RunStatus } from "@shared/types/runs";
import type { OverviewRuns, RunStatusCounts } from "@shared/types/overview";
import { ROUTES } from "@/lib/routes";
import { RunsIcon } from "@/lib/icons";
import { RunningNowTile } from "./RunningNowTile";
import { Fact, OverviewCard, OverviewLink, statusTone, type LinkTone } from "./OverviewCard";
import {
  RUN_BAR_SEGMENTS,
  runBarLabel,
  runBarSegments,
  runStatusWord,
  sevenDayLine,
  stripDayLabel,
  stripHeights,
} from "./overviewFormatting";

/** The two outcomes a reader acts on open the Runs list filtered to them (contract §3). */
const LINKED: ReadonlySet<RunStatus> = new Set<RunStatus>(["failed", "partially_succeeded"]);

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * #1409: the count says "in the last 24 hours", so its link carries the same window (`from`, the
 * list's own Started filter, shown there as a chip). Without it the list showed every failed run.
 */
export function runsLinkLast24h(status: RunStatus, now: number = Date.now()): string {
  const from = new Date(now - DAY_MS).toISOString();
  return `${ROUTES.runs}?${new URLSearchParams({ status, from }).toString()}`;
}

/**
 * A linked count's colour (round 4): its status's colour when it is not zero, the text colour at
 * zero. "Failed 0" in the link's amber read like a warning.
 */
function countTone(status: RunStatus, count: number): LinkTone {
  if (count === 0) return "neutral";
  return status === "failed" ? "destructive" : "warning";
}

/**
 * Runs, last 24 hours (contract §3, card 4): a stacked bar drawn with token classes, named in
 * words (`role="img"` + `aria-label`) so the colours are never the only signal, the counts
 * beside it, and the 7-day line. "Failed" and "Partly failed" link to `/runs?status=…&from=…` (the same 24 hours).
 */
export function RunsCard({ runs }: { runs: OverviewRuns }) {
  // The links' window is read again every minute, so a screen left open does not keep an old start.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const day = runs.last24h;
  const segments = runBarSegments(day);

  return (
    <OverviewCard
      id="runs"
      title="Runs, last 24 hours"
      icon={RunsIcon}
      tone={statusTone({
        failed: day.failed,
        warning: day.partially_succeeded,
        ok: day.succeeded,
      })}
      testId="overview-runs"
      action={<OverviewLink to={ROUTES.runs}>Open Runs</OverviewLink>}
    >
      {runs.active ? <RunningNowTile active={runs.active} /> : null}

      <div
        role="img"
        aria-label={runBarLabel(day)}
        className="flex h-3 w-full overflow-hidden rounded-full bg-muted"
        data-testid="overview-runs-bar"
      >
        {segments.map((segment) => (
          <div
            key={segment.status}
            className={`h-full ${segment.className}`}
            style={{ width: `${segment.percent}%` }}
            data-testid={`overview-runs-bar-${segment.status}`}
          />
        ))}
      </div>

      <dl className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-3 sm:grid-cols-3">
        {RUN_BAR_SEGMENTS.map(({ status }) => {
          const count = day[status];
          const word = runStatusWord(status);
          return (
            <Fact key={status} term={word} testId={`overview-runs-${status}`}>
              {LINKED.has(status) ? (
                <OverviewLink
                  to={runsLinkLast24h(status, now)}
                  testId={`overview-runs-link-${status}`}
                  tone={countTone(status, count)}
                  ariaLabel={`${count.toLocaleString()} ${word.toLowerCase()} in the last 24 hours. Open the Runs list filtered to ${word.toLowerCase()}.`}
                >
                  {count.toLocaleString()}
                </OverviewLink>
              ) : (
                count.toLocaleString()
              )}
            </Fact>
          );
        })}
      </dl>

      {runs.daily ? <RunsStrip daily={runs.daily} /> : null}

      <SkyText size={200} color="muted" block testId="overview-runs-7d">
        {sevenDayLine(runs)}
      </SkyText>
    </OverviewCard>
  );
}

/**
 * The last 7 days as a strip (round 4, owner 2026-09-30: "just a little more colorful will
 * help"): one column per rolling 24-hour window, oldest first, each as tall as its share of the
 * busiest day and split into the bar's own status colours. Colour is never the only signal: the
 * list is named, and each column carries its sentence (`stripDayLabel`) for a screen reader and
 * on hover.
 */
function RunsStrip({ daily }: { daily: readonly RunStatusCounts[] }) {
  const heights = stripHeights(daily);
  return (
    <div className="flex flex-col gap-1" data-testid="overview-runs-strip">
      <ol aria-label="Runs per day, last 7 days" className="flex h-10 items-end gap-1">
        {daily.map((counts, index) => {
          const label = stripDayLabel(counts, index, daily.length);
          return (
            <li
              key={index}
              title={label}
              className="flex h-full flex-1 items-end rounded-sm bg-muted"
            >
              <span className="sr-only">{label}</span>
              <div
                aria-hidden="true"
                data-testid={`overview-runs-strip-day-${index}`}
                className="flex w-full flex-col-reverse overflow-hidden rounded-sm"
                style={{ height: `${heights[index] ?? 0}%` }}
              >
                {runBarSegments(counts).map((segment) => (
                  <div
                    key={segment.status}
                    className={segment.className}
                    style={{ height: `${segment.percent}%` }}
                  />
                ))}
              </div>
            </li>
          );
        })}
      </ol>
      <div className="flex justify-between" aria-hidden="true">
        <SkyText size={200} color="muted">
          6 days ago
        </SkyText>
        <SkyText size={200} color="muted">
          Last 24 hours
        </SkyText>
      </div>
    </div>
  );
}
